import React, { useState, useEffect } from "react";
import { Card, CardContent, TextField, Button, FormControlLabel, Checkbox } from "@mui/material";
import axios from "axios"; // Assuming backend API for email sending

const EmailEditor = () => {
  const [htmlContent, setHtmlContent] = useState("<p>Type your email here...</p>");
  const [senderEmail, setSenderEmail] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [bulkEmails, setBulkEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [isBulk, setIsBulk] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/Header.css";
    link.id = "header-css";
    document.head.appendChild(link);

    return () => {
      document.getElementById("header-css")?.remove();
    };
  }, []);

  const validateEmails = (emailList) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailList.split(",").every((email) => emailRegex.test(email.trim()));
  };

  const validateSenderEmail = () => {
    if (!senderEmail.endsWith("@softup.agency")) {
      setError("Sender email must end with @softup.agency");
      return false;
    }
    setError("");
    return true;
  };

  const handleSendEmail = async () => {
    if (!validateSenderEmail()) return;

    if (isBulk) {
      if (!validateEmails(bulkEmails)) {
        setError("One or more emails in the bulk list are invalid.");
        return;
      }
    } else {
      if (!validateEmails(recipientEmail)) {
        setError("Recipient email is invalid.");
        return;
      }
    }

    const emailData = {
      sender: senderEmail,
      recipients: isBulk ? bulkEmails.split(",").map((email) => email.trim()) : [recipientEmail],
      subject,
      content: htmlContent,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/send-email`, emailData);
      alert(response.data.message || "Email sent successfully!");
    } catch (error) {
      console.error("Email sending failed", error);
      alert("Failed to send email. Please try again.");
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4 p-4">
      <Card className="p-4 col-span-2">
        <CardContent>
          <h2 className="text-xl font-bold mb-2">Sender Email</h2>
          <TextField
            fullWidth
            variant="outlined"
            label="Your Email (@softup.agency)"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            onBlur={validateSenderEmail}
            error={!!error}
            helperText={error}
          />
        </CardContent>
      </Card>

      <Card className="p-4 col-span-2">
        <CardContent>
          <FormControlLabel
            control={<Checkbox checked={isBulk} onChange={() => setIsBulk(!isBulk)} />}
            label="Send Bulk Emails"
          />
        </CardContent>
      </Card>

      {!isBulk && (
        <Card className="p-4 col-span-2">
          <CardContent>
            <h2 className="text-xl font-bold mb-2">Recipient Email</h2>
            <TextField
              fullWidth
              variant="outlined"
              label="Recipient Email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
            />
          </CardContent>
        </Card>
      )}

      {isBulk && (
        <Card className="p-4 col-span-2">
          <CardContent>
            <h2 className="text-xl font-bold mb-2">Bulk Email List</h2>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              label="Enter emails separated by commas"
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
            />
          </CardContent>
        </Card>
      )}

      <Card className="p-4 col-span-2">
        <CardContent>
          <h2 className="text-xl font-bold mb-2">Email Subject</h2>
          <TextField
            fullWidth
            variant="outlined"
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </CardContent>
      </Card>

      <Card className="p-4 col-span-2">
        <CardContent>
          <h2 className="text-xl font-bold mb-2">Email Editor</h2>
          <TextField
            fullWidth
            multiline
            minRows={10}
            variant="outlined"
            className="border p-2 text-start align-top"
            value={htmlContent}
            onChange={(e) => setHtmlContent(e.target.value)}
          />
        </CardContent>
      </Card>

      <Card className="p-4 col-span-2">
        <CardContent>
          <h2 className="text-xl font-bold mb-2">Email Preview</h2>
          <div className="border p-4 h-[60vh] overflow-auto" dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </CardContent>
      </Card>

      <div className="col-span-2 flex justify-end">
        <Button variant="contained" color="primary" onClick={handleSendEmail}>
          Send Email
        </Button>
      </div>
    </div>
  );
};

export default EmailEditor;
